import Badge from "../common/Badge";
import Button from "../common/Button";
import EmptyState from "../common/EmptyState";
import { getSummaryPreview } from "../../utils/callDisplay";
import {
  getCampaignCallStatusMeta,
  getCampaignOutcomeMeta,
  getCampaignStatusMeta,
} from "../../utils/helpers";
import { formatCampaignCallDuration } from "../../utils/helpers";

import { FiPhone } from "react-icons/fi";
import { FiCheckCircle } from "react-icons/fi";
import { MdOutlineLightbulb } from "react-icons/md";
import { MdOutlineCalendarToday } from "react-icons/md";
import { MdThumbDown } from "react-icons/md";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";

export default function CampaignDetail({ campaign, calls, loading, actionBusy, onBack, onAction }) {
  if (loading || !campaign) {
    return (
      <div>
        <div className="subsection-row">
          <Button onClick={onBack}>&larr; Back</Button>
          <div className="section-title section-title-no-margin">
            Campaign Detail
          </div>
        </div>
        <div className="table-wrap">
          <EmptyState
            title={loading ? "Loading campaign..." : "No campaign available"}
            subtitle={loading ? "Please wait." : "Campaign details will appear here when data is available."}
          />
        </div>
      </div>
    );
  }

  const status = getCampaignStatusMeta(campaign.status);
  const percentage = campaign.total_targets
    ? Math.round((campaign.calls_made / campaign.total_targets) * 100)
    : 0;

  return (
    <div>
      <div className="subsection-row">
        <Button onClick={onBack}>&larr; Back</Button>
        <div className="section-title section-title-no-margin">
          {campaign.name}
        </div>
      </div>

      <div className="campaign-offer-row">
        <span className="campaign-offer-label">Offer:</span>
        <span className="campaign-offer-text">{campaign.offer_message}</span>
      </div>

      <div className="stats-grid detail-stats-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <div className="stat-card">
          <div className="stat-icon gold">
            <FiPhone className="w-5 h-5" />
          </div>
          <div className="stat-body">
            <div className="stat-label">Total / Made</div>
            <div className="stat-value">{`${campaign.calls_made}/${campaign.total_targets}`}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <FiCheckCircle className="w-5 h-5" />
          </div>
          <div className="stat-body">
            <div className="stat-label">Answered</div>
            <div className="stat-value">{campaign.calls_answered}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue">
            <MdOutlineLightbulb className="w-5 h-5" />
          </div>
          <div className="stat-body">
            <div className="stat-label">Interested</div>
            <div className="stat-value">{campaign.calls_interested}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <MdOutlineCalendarToday className="w-5 h-5" />
          </div>
          <div className="stat-body">
            <div className="stat-label">Booked</div>
            <div className="stat-value">{campaign.calls_booked}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon amber">
            <MdThumbDown className="w-5 h-5" />
          </div>
          <div className="stat-body">
            <div className="stat-label">Declined</div>
            <div className="stat-value">{campaign.calls_declined}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon red">
            <HiOutlinePhoneMissedCall className="w-5 h-5" />
          </div>
          <div className="stat-body">
            <div className="stat-label">Failed</div>
            <div className="stat-value">{campaign.calls_failed}</div>
          </div>
        </div>
      </div>

      <div className="campaign-progress-row">
        <div className="campaign-progress-track">
          <div
            className="campaign-progress-fill"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="campaign-progress-pct">{percentage}%</span>
      </div>
      {campaign.scheduled_at && campaign.status === "scheduled" && (
        <div className="campaign-offer-row">
          <span className="campaign-offer-label">Scheduled:</span>
          <span className="campaign-offer-text">
            {new Date(campaign.scheduled_at).toLocaleString()} — will start automatically
          </span>
        </div>
      )}

      <div style={{ marginTop:'2%', marginBottom:'2%', display:'flex', gap:'1%', alignItems:'center' }} className="campaign-detail-actions">
        {campaign.status === "draft" ? (
          <Button variant="green" onClick={() => onAction("start")} disabled={actionBusy}>
            {actionBusy ? "Starting..." : "Start Campaign"}
          </Button>
        ) : null}
        {campaign.status === "scheduled" ? (
          <Button variant="danger" onClick={() => onAction("cancel")} disabled={actionBusy}>
            {actionBusy ? "Cancelling..." : "Cancel Schedule"}
          </Button>
        ) : null}
        {campaign.status === "running" ? (
          <>
            <Button variant="primary" onClick={() => onAction("pause")} disabled={actionBusy}>
              {actionBusy ? "Pausing..." : "Pause"}
            </Button>
            <Button variant="danger" onClick={() => onAction("cancel")} disabled={actionBusy}>
              {actionBusy ? "Cancelling..." : "Cancel"}
            </Button>
          </>
        ) : null}
        {campaign.status === "paused" ? (
          <>
            <Button variant="green" onClick={() => onAction("resume")} disabled={actionBusy}>
              {actionBusy ? "Resuming..." : "Resume"}
            </Button>
            <Button variant="danger" onClick={() => onAction("cancel")} disabled={actionBusy}>
              {actionBusy ? "Cancelling..." : "Cancel"}
            </Button>
          </>
        ) : null}
        <Badge variant={status.variant}>{status.label}</Badge>
      </div>

      <div className="section-title">Call Results</div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Outcome</th>
              <th>Duration</th>
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {!calls.length ? (
              <tr>
                <td colSpan="6">
                  <EmptyState
                    title="No calls yet"
                    subtitle="Campaign call results will appear here."
                  />
                </td>
              </tr>
            ) : (
              calls.map((call) => {
                const callStatus = getCampaignCallStatusMeta(call.status);
                const outcome = getCampaignOutcomeMeta(call.outcome);

                return (
                  <tr key={call.id || `${call.phone}-${call.name}`}>
                    <td>{call.name}</td>
                    <td className="td-phone">{call.phone}</td>
                    <td>
                      <Badge variant={callStatus.variant}>
                        {callStatus.label}
                      </Badge>
                    </td>
                    <td>
                      <Badge variant={outcome.variant}>{outcome.label}</Badge>
                    </td>
                    <td>{formatCampaignCallDuration(call.duration_seconds)}</td>
                    <td className="td-summary">
                      {call.summary ? getSummaryPreview(call.summary, 80) : "--"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
